import { ComponentType, JSX, h, Fragment } from "preact"
import { KeywordInCategory, KeywordInCategoryProps } from "./KeywordInCategory"
import { KeywordWrapper } from "./KeywordWrapper"
import { WrapperProps } from "./types"
import type { SearchKeyword, SearchTermsFacet } from "@nosto/nosto-js/client"

/**
 * @group Components
 */
export interface KeywordsInCategoryProps {
    /**
     * Field name for category suggestion.
     */
    categoryField: string
    /**
     * Keyword object.
     */
    hit: SearchKeyword
    /**
     * Component to wrap keyword content component. For e.g. AutocompleteElement.
     */
    wrapperComponent?: ComponentType<WrapperProps>
    /**
     * Component for rendering keywords visual content.
     */
    keywordComponent: JSX.Element
    /**
     * Component for rendering keyword with category suggestion (default: KeywordInCategoryDefault)
     */
    keywordInCategoryComponent?: ComponentType<KeywordInCategoryProps>
    /**
     * Number of category suggestions to render (default: 3)
     */
    categorySuggestionCount?: number
}

/**
 * Component for rendering keywords with category suggestion.
 *
 * @example
 * ```jsx
 * <KeywordsInCategory
 *  hit={firstKeyword}
 *  categoryField={categoryField}
 *  keywordComponent={<Keyword hit={firstKeyword} />}
 *  wrapperComponent={({ keywordComponent, filter }) => (
 *      <Element
 *          keywordComponent={keywordComponent}
 *          hit={firstKeyword}
 *          filter={filter}
 *      />
 *  )}
 *  keywordInCategoryComponent={keywordInCategoryComponent}
 * />
 * ```
 *
 * @group Components
 */
export function KeywordsInCategory({
    categoryField,
    wrapperComponent,
    hit,
    keywordComponent,
    keywordInCategoryComponent: KeywordInCategoryComponent = KeywordInCategory,
    categorySuggestionCount = 3
}: KeywordsInCategoryProps) {
    const facet = hit.facets?.find((f): f is SearchTermsFacet => "data" in f && f.field === categoryField)

    if (facet?.data?.length) {
        return (
            <>
                <KeywordWrapper wrapperComponent={wrapperComponent}>{keywordComponent}</KeywordWrapper>
                {facet.data.slice(0, categorySuggestionCount).map((category, index) => {
                    return (
                        <KeywordWrapper
                            wrapperComponent={wrapperComponent}
                            key={category.value ?? index}
                            filter={{
                                value: [category.value],
                                field: categoryField
                            }}
                        >
                            <KeywordInCategoryComponent category={category}>
                                {keywordComponent}
                            </KeywordInCategoryComponent>
                        </KeywordWrapper>
                    )
                })}
            </>
        )
    }

    return <KeywordWrapper wrapperComponent={wrapperComponent}>{keywordComponent}</KeywordWrapper>
}
